import React from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import {
  Building2,
  Hammer,
  Briefcase,
  Home,
  ArrowRight,
  Award,
  ShieldCheck,
  HardHat,
  CheckCircle2,
  BadgeCheck,
  Globe,
  MapPin,
  Mail,
  Compass
} from 'lucide-react';

const PROJECT_EXPERIENCE = [
  {
    title: 'Residential Builds',
    icon: Home,
    image:
      'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200&q=80&auto=format&fit=crop',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    blurb: 'New homes built right — from first review to handover.',
    services: ['Reviews', 'Approvals', 'Completion', 'Defects', 'Help', 'Guidance']
  },
  {
    title: 'Commercial Builds',
    icon: Briefcase,
    image:
      'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1200&q=80&auto=format&fit=crop',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blurb: 'Warehouses, retail and mixed-use builds, delivered clean.',
    services: ['Reviews', 'Approvals', 'Completion', 'Defects', 'Help', 'Guidance']
  },
  {
    title: 'Refurbishments & Repairs',
    icon: Hammer,
    image:
      'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=1200&q=80&auto=format&fit=crop',
    color: 'bg-amber-50 text-amber-800 border-amber-200',
    blurb: 'Upgrades, rework and repairs — without disrupting your day.',
    services: ['Reviews', 'Approvals', 'Completion', 'Defects', 'Help', 'Guidance']
  },
  {
    title: 'Strata Defects & Repair Works',
    icon: Building2,
    image:
      'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&q=80&auto=format&fit=crop',
    color: 'bg-sky-50 text-sky-700 border-sky-200',
    blurb: 'Strata building defect inspections, reports and supervised fixes for owners and committees.',
    services: ['Reviews', 'Approvals', 'Completion', 'Defects', 'Help', 'Guidance']
  },
  {
    title: 'DIY Projects or Project Management',
    icon: HardHat,
    image:
      'https://images.unsplash.com/photo-1572177812156-58036aae439c?w=1200&q=80&auto=format&fit=crop',
    color: 'bg-rose-50 text-rose-700 border-rose-200',
    blurb: 'Hands-on DIY support or full project management — pick the level of help that suits you.',
    services: ['Reviews', 'Approvals', 'Completion', 'Defects', 'Help', 'Guidance']
  }
];

const STATS = [
  { value: '20+', label: 'Years on the tools' },
  { value: '100+', label: 'Projects delivered' },
  { value: '5+', label: 'Project areas covered' },
  { value: 'A$30M+', label: 'Overall projects managed' }
];

export default function About() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div data-testid="about-page" className="min-h-screen bg-slate-50">
      {user ? (
        <Navigation />
      ) : (
        <nav className="fixed top-0 w-full z-50 bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-lg font-bold"
              style={{ fontFamily: 'Outfit' }}
            >
              <Compass className="w-6 h-6" strokeWidth={2.25} />
              All Build Consult
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/services')}
                className="px-4 py-2 text-sm text-black hover:text-blue-600 transition-colors"
              >
                Services
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-black text-white rounded-sm hover:bg-slate-800 transition-all text-sm font-medium"
              >
                Sign In
              </button>
            </div>
          </div>
        </nav>
      )}

      <div className={`max-w-7xl mx-auto px-6 py-8 ${user ? 'mt-16' : 'pt-24'}`}>
        {/* Hero */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 bg-black text-white rounded-sm text-xs font-semibold uppercase tracking-wider">
            <Award className="w-3 h-3" />
            About All Build Consult
          </div>
          <h1
            className="text-5xl lg:text-6xl font-bold tracking-tight text-black mb-5 max-w-4xl"
            style={{ fontFamily: 'Outfit' }}
          >
            Real builders. Real reviews. Real help.
          </h1>
          <p className="text-lg text-slate-700 max-w-3xl leading-relaxed">
            All Build Consult helps homeowners, investors and small builders with simple,
            independent advice: reviews, approvals, completion checks, defects, help and guidance —
            backed by a licensed builder and operator.
          </p>
        </div>

        {/* Stats strip */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {STATS.map((s) => (
            <div key={s.label} data-testid="about-stat" className="grid-border bg-white p-5">
              <div className="text-3xl font-bold text-black mb-1" style={{ fontFamily: 'Outfit' }}>
                {s.value}
              </div>
              <div className="text-xs text-slate-600 uppercase tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Licensed Builder & Operator banner */}
        <div
          data-testid="licensed-builder-banner"
          className="grid-border bg-white p-0 overflow-hidden mb-16 grid md:grid-cols-2"
        >
          <div className="relative h-64 md:h-auto">
            <img
              src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80&auto=format&fit=crop"
              alt="Licensed builder on site wearing hard hat reviewing plans"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 bg-white/95 backdrop-blur rounded-sm text-xs font-semibold uppercase tracking-wider text-black shadow">
              <BadgeCheck className="w-3.5 h-3.5 text-blue-600" />
              Licensed Builder &amp; Operator
            </div>
          </div>
          <div className="p-8 flex flex-col justify-center">
            <h2
              className="text-3xl font-bold text-black mb-3"
              style={{ fontFamily: 'Outfit' }}
            >
              Built by a licensed builder.
            </h2>
            <p className="text-slate-700 mb-5 leading-relaxed">
              The team at All Build Consult is a <strong>licensed builder and operator</strong> —
              every review, report and recommendation is done by someone who has actually built,
              fixed and finished projects on the tools and in the office.
            </p>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <ShieldCheck className="w-4 h-4 text-green-600" />
                Independent — we don't sell you a build, we protect your project
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <HardHat className="w-4 h-4 text-green-600" />
                On-site experience across new builds, repairs and insurance works
              </li>
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Plain-English reviews, approvals support and defect guidance
              </li>
            </ul>
          </div>
        </div>

        {/* Project Experience */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-black mb-2" style={{ fontFamily: 'Outfit' }}>
            Experience &amp; Services
          </h2>
          <p className="text-slate-600 max-w-2xl">
            Where we've worked, and the simple help we provide on every job.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {PROJECT_EXPERIENCE.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid="project-experience-card"
                className="grid-border bg-white overflow-hidden hover-lift flex flex-col"
              >
                <div className="relative h-48 bg-slate-100">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div
                    className={`absolute top-3 left-3 w-10 h-10 rounded-sm flex items-center justify-center border ${p.color} bg-white/95 backdrop-blur`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3
                    className="text-xl font-semibold text-black mb-2"
                    style={{ fontFamily: 'Outfit' }}
                  >
                    {p.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">{p.blurb}</p>
                  <div className="mt-auto pt-3 border-t border-slate-100">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                      We help with
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {p.services.map((s) => (
                        <span
                          key={s}
                          data-testid="service-tag"
                          className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[11px] font-medium rounded-sm"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Online service · Service area · Contact */}
        <div
          data-testid="about-contact-section"
          className="grid md:grid-cols-3 gap-4 mb-16"
        >
          <div className="grid-border bg-white p-6">
            <div className="w-10 h-10 bg-blue-50 rounded-sm flex items-center justify-center mb-3">
              <Globe className="w-5 h-5 text-blue-700" />
            </div>
            <h3 className="text-base font-semibold text-black mb-1" style={{ fontFamily: 'Outfit' }}>
              Online Service
            </h3>
            <p className="text-sm text-slate-600">
              All Build Consult is an <strong>online service company</strong>. Every consultation,
              review and report is delivered remotely through this website — fast, paperless, and
              on your schedule.
            </p>
          </div>
          <div className="grid-border bg-white p-6">
            <div className="w-10 h-10 bg-emerald-50 rounded-sm flex items-center justify-center mb-3">
              <MapPin className="w-5 h-5 text-emerald-700" />
            </div>
            <h3 className="text-base font-semibold text-black mb-1" style={{ fontFamily: 'Outfit' }}>
              Service Area
            </h3>
            <p className="text-sm text-slate-600">
              Serving <strong>Australia only</strong>, specialising in the <strong>NSW &amp; ACT</strong>
              regions — with local building, council and trade knowledge baked in.
            </p>
          </div>
          <div
            data-testid="about-contact-card"
            className="grid-border bg-white p-6"
          >
            <div className="w-10 h-10 bg-rose-50 rounded-sm flex items-center justify-center mb-3">
              <Mail className="w-5 h-5 text-rose-700" />
            </div>
            <h3 className="text-base font-semibold text-black mb-1" style={{ fontFamily: 'Outfit' }}>
              Contact
            </h3>
            <p className="text-sm text-slate-600 mb-2">
              Have a question before booking? Reach out directly.
            </p>
            <a
              data-testid="about-contact-email"
              href="mailto:admin@allbuildconsult.net"
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline break-all"
            >
              <Mail className="w-4 h-4" />
              admin@allbuildconsult.net
            </a>
            <div
              data-testid="about-contact-abn"
              className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600"
            >
              <span className="block text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-0.5">
                ABN
              </span>
              <span className="font-mono text-sm text-black tracking-wider">78 138 457 093</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="grid-border bg-blue-600 text-white p-10 text-center">
          <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
            Ready to get a builder on your side?
          </h2>
          <p className="text-blue-100 max-w-2xl mx-auto mb-6">
            Start with a quick enquiry. We'll review it, send you a Tax Invoice and have an initial
            report back to you fast.
          </p>
          <button
            data-testid="about-cta-start"
            onClick={() => navigate(user ? '/bookings' : '/')}
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-blue-700 rounded-sm hover:bg-slate-100 transition-all font-semibold"
          >
            {user ? 'Start a Pre-Assessment' : 'Get Started'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
